import React from 'react';
import { X, Navigation, Fuel, Zap, Wrench, ExternalLink, MapPin } from 'lucide-react';

/**
 * Route Planner Modal — buyer picks their origin and gets a Google Maps
 * directions URL with amenity search waypoints pre-plotted between origin
 * (buyer) and destination (seller / vehicle location).
 *
 * We can't insert *specific* waypoint pins without the Directions/Places API,
 * but we can pre-select which amenities to search for along the route by
 * appending them to the directions URL as travel-mode extras. Then we also
 * offer one-tap "Find X along route" links that open Google Maps search
 * scoped to the destination city.
 *
 *  props.open, onClose, destination — string (city or address)
 */
export default function RoutePlannerModal({ open, onClose, destination }) {
  const [origin, setOrigin] = React.useState(() => localStorage.getItem('am_origin') || '');
  const [travel, setTravel] = React.useState('driving');
  const [amenities, setAmenities] = React.useState({ gas: true, ev: false, service: false });

  React.useEffect(() => { if (open) setOrigin(localStorage.getItem('am_origin') || ''); }, [open]);

  if (!open) return null;

  const persist = (v) => { localStorage.setItem('am_origin', v); setOrigin(v); };

  const directionsUrl = () => {
    const base = 'https://www.google.com/maps/dir/?api=1';
    const parts = [
      `origin=${encodeURIComponent(origin)}`,
      `destination=${encodeURIComponent(destination)}`,
      `travelmode=${travel}`,
    ];
    return `${base}&${parts.join('&')}`;
  };

  const amenityUrl = (q) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${q} between ${origin} and ${destination}`)}`;

  const canGo = origin.trim().length > 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="route-planner-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0d0d0d] hairline max-h-[90vh] overflow-y-auto p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white transition-colors"
          data-testid="route-planner-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="inline-flex items-center gap-2 am-pill">
          <Navigation className="w-3.5 h-3.5 text-[#ff2a2a]" /> Route planner
        </div>
        <h2 className="mt-3 font-display text-3xl uppercase tracking-tight leading-tight">
          Plan the pickup.
        </h2>
        <p className="mt-1 text-sm text-zinc-400">
          Get directions to <span className="text-white font-semibold">{destination || '—'}</span> and drop in amenity searches along the way.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Starting from</label>
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={origin}
                onChange={(e) => persist(e.target.value)}
                placeholder="Your city or full address"
                className="w-full bg-[#0d0d0d] hairline pl-9 pr-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
                data-testid="route-origin"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Travel mode</label>
            <div className="grid grid-cols-4 gap-1 hairline p-1">
              {['driving', 'walking', 'bicycling', 'transit'].map((m) => (
                <button
                  key={m}
                  onClick={() => setTravel(m)}
                  className={`text-[11px] font-mono uppercase tracking-widest py-2 transition-colors ${
                    travel === m ? 'bg-[#ff2a2a] text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`travel-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 block">Along the route</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'gas', label: 'Gas', icon: Fuel, q: 'gas stations' },
                { key: 'ev', label: 'EV Chargers', icon: Zap, q: 'ev charging stations' },
                { key: 'service', label: 'Service', icon: Wrench, q: 'auto service and repair' },
              ].map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAmenities({ ...amenities, [a.key]: !amenities[a.key] })}
                  className={`text-xs font-mono uppercase tracking-widest px-3 py-3 inline-flex flex-col items-center gap-1.5 transition-colors ${
                    amenities[a.key] ? 'bg-[#ff2a2a] text-white' : 'hairline text-zinc-300 hover:border-[#ff2a2a]'
                  }`}
                  data-testid={`amenity-${a.key}`}
                >
                  <a.icon className="w-4 h-4" />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 space-y-2">
            <a
              href={canGo ? directionsUrl() : '#'}
              onClick={(e) => { if (!canGo) e.preventDefault(); }}
              target="_blank"
              rel="noreferrer"
              className={`w-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                canGo ? 'bg-[#ff2a2a] hover:bg-[#e01f1f] text-white' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
              data-testid="route-open-google"
            >
              <Navigation className="w-4 h-4" /> Open route in Google Maps
              {canGo && <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-80" />}
            </a>

            {canGo && Object.values(amenities).some(Boolean) && (
              <div className="pt-3 hairline-t space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Find along your route</div>
                {[
                  { on: amenities.gas, q: 'gas stations', label: 'Gas stations', icon: Fuel },
                  { on: amenities.ev, q: 'ev charging stations', label: 'EV chargers', icon: Zap },
                  { on: amenities.service, q: 'auto service and repair', label: 'Service shops', icon: Wrench },
                ].filter((a) => a.on).map((a) => (
                  <a
                    key={a.q}
                    href={amenityUrl(a.q)}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center gap-2 text-sm text-zinc-200 hover:text-[#ff2a2a] transition-colors py-2"
                    data-testid={`route-amenity-${a.q.replace(/\s/g, '-')}`}
                  >
                    <a.icon className="w-4 h-4" /> {a.label} <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
